import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";

/**
 * Car Airport Service
 * Araç ilçe sayfaları içerik servisleri
 */
export const carAirportService = {

  /**
   * Havalimani bilgilerini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   *   "Slug": "istanbul"
   * }
   */
  getAirportLocationInfo: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getAirportLocationInfo,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Tüm kampanyaları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   * }
   */
  getCampaigns: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getCampaigns,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler kiralık araç listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   *   "CurrencyCode": "TRY"
   * }
   */
  getPopularCars: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getPopularCars,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * İlçe analytics bilgilerini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   *   "CurrencyCode": "TRY"
   * }
   */
  getAirportAnalytics: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getAirportAnalytics,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler kiralama noktaları listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   * }
   */
  getRentalLocations: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getRentalLocations,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Tüm ayrıcalıkları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr"
   * }
   */
  getPrivileges: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getPrivileges,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler firma listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   * }
   */
  getPopularCompanies: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getCompanies,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler tatil bölgelerini getirir
   * @param {Object} payload - API payload
   * @param {string} [payload.LanguageCode] - Dil kodu (örn: "tr", "en")
   * @param {string} [payload.CurrencyCode] - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Özel hata yönetimi kullanılsın mı
   * @returns {Promise<any>} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "CurrencyCode": "TRY"
   * }
   */
  getPopularLocations: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getLocations,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Blog yazılarını getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.ClientChannel - İstemci kanalı (örn: "web", "mobile")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "ClientChannel": "web",
   *   "CurrencyCode": "TRY"
   * }
   */
  getBlog: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getBlog,
      payload,
      headers,
      hasErrorHandler,
    }),


  /**
   * Neden Turna sayfası içeriğini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr"
   * }
   */
  getWhyTurna: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getWhyTurna,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Sık sorulan soruları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "CurrencyCode": "TRY"
   * }
   */
  getSss: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.airport.getSss,
      payload,
      headers,
      hasErrorHandler,
    }),
};

